import React, { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Filter } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const ACTORS = ["", "superadmin", "reseller", "agent", "system"];

export default function AuditPage() {
    const [actor, setActor] = useState("");
    const [action, setAction] = useState("");
    const qs = new URLSearchParams();
    if (actor) qs.set("actor_type", actor);
    if (action) qs.set("action", action);
    qs.set("limit", "500");
    const { data, isLoading } = useSWR(`/api/admin/audit?${qs}`, fetcher, {
        refreshInterval: 4000,
    });

    return (
        <div data-testid="audit-page">
            <PageHeader
                testid="audit-header"
                title="Audit log"
                subtitle="Every state change. Filter by actor or action."
            />

            <div className="px-4 sm:px-6 py-3 flex items-center gap-2">
                <Filter size={14} className="text-zinc-500" />
                <select
                    data-testid="audit-actor-filter"
                    className="cm-input max-w-[180px]"
                    value={actor}
                    onChange={(e) => setActor(e.target.value)}
                >
                    {ACTORS.map((a) => (
                        <option key={a} value={a}>
                            {a || "all actors"}
                        </option>
                    ))}
                </select>
                <input
                    data-testid="audit-action-filter"
                    className="cm-input max-w-xs font-mono"
                    placeholder="action (regex), e.g. cloud."
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                />
                <div className="ml-auto text-xs font-mono text-zinc-500">
                    {data?.length || 0} events
                </div>
            </div>

            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                {isLoading ? (
                    <Spinner />
                ) : !data?.length ? (
                    <Empty title="No audit events" />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Timestamp</th>
                                    <th className="cm-th">Actor</th>
                                    <th className="cm-th">Action</th>
                                    <th className="cm-th">Resource</th>
                                    <th className="cm-th">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((e) => (
                                    <tr key={e.id} data-testid={`audit-row-${e.id}`}>
                                        <td className="cm-td font-mono text-xs text-zinc-500" title={e.ts}>
                                            {fmtRel(e.ts)}
                                        </td>
                                        <td className="cm-td font-mono text-xs">
                                            <span className="text-zinc-300">{e.actor_type}</span>
                                            <span className="text-zinc-500"> · {(e.actor_id || "").slice(0, 8)}</span>
                                        </td>
                                        <td className="cm-td font-mono text-xs text-zinc-100">{e.action}</td>
                                        <td className="cm-td font-mono text-xs text-zinc-400">
                                            {e.resource_type ? (
                                                <>
                                                    {e.resource_type}
                                                    <span className="text-zinc-600">/</span>
                                                    {(e.resource_id || "").slice(0, 8)}
                                                </>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="cm-td font-mono text-[11px] text-zinc-500 max-w-md break-all">
                                            {Object.keys(e.details || {}).length
                                                ? JSON.stringify(e.details)
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
